import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import { useSnackbar } from 'notistack'
import palette from '../../../theme/palette'
import { filterButtonSx } from '../../common/sharedStyles'
import { useAddRecipientMutation, useLookupRecipientQuery } from '../../redux/internalAlertsApiSlice'
import { HEADER_TYPES, isEmailChannel } from './alertUtils'

const EMAIL_RE = /^[a-z0-9._%+\-']+@[a-z0-9.\-]+\.[a-z]{2,}$/i

/**
 * Add one recipient to an alert.
 * WhatsApp / SMS: mobile is +91 + 10 digits (the only form the senders use); the name is what the greeting will say.
 * Email: any address — a person or a group mailbox (ProshipTechTeam@…) — plus which header slot it goes in
 * (To / CC / BCC, as the old *.to/cc/bcc properties did).
 * Once the identifier is complete we ask the backend whether this is a Proship user and prefill the name —
 * recipients do NOT have to be Proship users, the lookup is only a convenience.
 */
export default function RecipientForm({ templateName, channel = 'WHATSAPP', existingKeys = [], onDone, onCancel }) {
  const { enqueueSnackbar } = useSnackbar()
  const email = isEmailChannel(channel)
  const [digits, setDigits] = useState('')
  const [address, setAddress] = useState('')
  const [headerType, setHeaderType] = useState('TO')
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [reason, setReason] = useState('')

  const addressTrim = address.trim().toLowerCase()
  const complete = email ? EMAIL_RE.test(addressTrim) : digits.length === 10
  const key = email ? addressTrim : (complete ? `+91${digits}` : null)
  const { data: lookup, isFetching: looking } = useLookupRecipientQuery(key, { skip: !complete })
  useEffect(() => {
    if (complete && lookup?.found && lookup.displayName && !nameTouched) setName(lookup.displayName)
  }, [complete, lookup, nameTouched])

  const duplicate = complete && existingKeys.map(k => String(k).toLowerCase()).includes(String(key).toLowerCase())

  const [add, { isLoading }] = useAddRecipientMutation()
  const submit = async (e) => {
    e?.preventDefault?.()
    if (!complete || duplicate) return
    try {
      const body = { templateName, displayName: name.trim() || undefined, reason: reason.trim() || undefined }
      if (email) { body.email = key; body.headerType = headerType } else { body.mobile = key }
      await add(body).unwrap()
      enqueueSnackbar(`${name.trim() || key} added${email ? ` (${headerType})` : ''} — takes effect on the next run`, { variant: 'success' })
      onDone?.()
    } catch (err) {
      enqueueSnackbar(err?.data?.message || err?.message || 'Could not add the recipient', { variant: err?.status === 409 ? 'warning' : 'error', autoHideDuration: 8000 })
    }
  }

  const helper = !complete ? (email ? (address ? 'Enter a full address' : 'Person or group mailbox') : `${digits.length}/10 digits`)
    : looking ? 'Checking Proship users…'
    : duplicate ? 'Already an active recipient of this alert'
    : lookup?.found ? `Proship user${email ? (lookup.mobile ? ` · ${lookup.mobile}` : '') : (lookup.email ? ` · ${lookup.email}` : '')}`
    : email ? 'Not a Proship user — fine for a group mailbox or an external address' : 'Not a Proship user — that is fine, just enter a name'

  return (
    <Box component='form' onSubmit={submit} sx={{ p: 2, border: `1px solid ${palette.primary.light}`, borderRadius: '12px', bgcolor: '#F7FAFF' }}>
      <Typography sx={{ color: palette.primary.main, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem', display: 'block', mb: 1 }}>
        Add recipient
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {email ? (
          <>
            <TextField
              autoFocus size='small' label='Email address' value={address} type='email'
              onChange={(e) => setAddress(e.target.value)}
              inputProps={{ maxLength: 120, style: { fontFamily: 'monospace' }, autoCapitalize: 'none' }}
              error={Boolean(duplicate)} helperText={helper} sx={{ width: 300 }}
            />
            <TextField select size='small' label='Header' value={headerType} onChange={(e) => setHeaderType(e.target.value)} sx={{ width: 110 }}
              helperText='To, CC or BCC'>
              {HEADER_TYPES.map(h => <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>)}
            </TextField>
          </>
        ) : (
          <TextField
            autoFocus size='small' label='Mobile' value={digits}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
            InputProps={{ startAdornment: <InputAdornment position='start'>+91</InputAdornment> }}
            inputProps={{ inputMode: 'numeric', maxLength: 10, style: { fontFamily: 'monospace' } }}
            error={Boolean(duplicate)} helperText={helper} sx={{ width: 220 }}
          />
        )}
        <TextField
          size='small' label={email ? 'Name (optional)' : 'Name (as the greeting will read)'} value={name}
          onChange={(e) => { setName(e.target.value); setNameTouched(true) }}
          inputProps={{ maxLength: 80 }} sx={{ width: 260 }}
        />
        <TextField size='small' label='Reason (optional)' value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 300 }} sx={{ flex: 1, minWidth: 200 }} helperText='Recorded in the change log' />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button size='small' sx={filterButtonSx('reset')} onClick={onCancel} disabled={isLoading}>Cancel</Button>
        <Button size='small' type='submit' variant='contained' startIcon={<PersonAddAltOutlinedIcon />} disabled={!complete || Boolean(duplicate) || isLoading} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {isLoading ? 'Adding…' : 'Add'}
        </Button>
      </Box>
    </Box>
  )
}
